<template>
  <div id="outsideContainer" class="border word-cloze-page">
    <div class="set-location card-header">
      <div class="inside-header-left">
        <span class="andover-logo-area" @click="isAssignmentSession ? goAssignmentList() : goHome()">
          <img style="width: 20px; height: 20px" src="../andover.png" alt="home" />
        </span>
      </div>
      <div class="navi-text">
        <span>{{ payload?.subjectName || '完形填空' }}</span>
        <span v-if="payload?.setName"> &gt; {{ payload.setName }}</span>
      </div>
    </div>

    <div class="card-wrapper">
      <div class="card-content cloze-content">
        <div v-if="loadError" class="cloze-panel">
          <p>{{ loadError }}</p>
          <el-button type="primary" @click="goHome">返回首页</el-button>
        </div>

        <div v-else-if="assignmentLoading" class="cloze-panel" v-loading="true">
          <p>加载完形填空作业...</p>
        </div>

        <div v-else-if="phase === 'intro'" class="cloze-panel">
          <h2>完形填空</h2>
          <p class="intro-desc">
            例句中挖去的词会给出开头几个字母，请把后面的字母填完整。每组 {{ groupSize }} 个词，共
            {{ groups.length }} 组。
          </p>
          <div class="intro-notice">
            <p><strong>练习说明</strong></p>
            <ul>
              <li>挖空处显示的是例句里的<strong>原词形</strong>（如 speeding、languages），不是词表原形。</li>
              <li>不超过 5 个字母的词给出开头 <strong>2</strong> 个字母，超过 5 个字母给出开头 <strong>3</strong> 个字母；其余每个字母各填一格，大小写不区分对错。</li>
            </ul>
          </div>
          <div class="intro-config-card">
            <div class="intro-config-grid">
              <div class="intro-stat-item">
                <span class="intro-stat-value">{{ payload?.items?.length || 0 }}</span>
                <span class="intro-stat-label">带例句词汇</span>
              </div>
              <div class="intro-stat-divider" aria-hidden="true"></div>
              <div class="intro-stat-item">
                <span class="intro-stat-value">{{ exerciseItemCount }}</span>
                <span class="intro-stat-label">练习题数</span>
              </div>
              <div class="intro-stat-divider" aria-hidden="true"></div>
              <div class="intro-stat-item">
                <span class="intro-stat-value">{{ groups.length }}</span>
                <span class="intro-stat-label">练习组数</span>
              </div>
            </div>
            <div class="intro-time-row">
              <div class="intro-time-label-shell">
                <span class="intro-time-badge">
                  <el-icon :size="16"><Timer /></el-icon>
                </span>
                <div class="intro-time-text">
                  <span class="intro-time-title">每组限时</span>
                </div>
              </div>
              <div class="intro-time-input-shell">
                <template v-if="isAssignmentSession">
                  <span class="intro-time-field intro-time-locked">{{ timeLimitSeconds }}</span>
                  <span class="intro-time-suffix">秒</span>
                </template>
                <template v-else>
                  <input
                    v-model.number="timeLimitSeconds"
                    class="intro-time-field"
                    type="number"
                    step="5"
                  />
                  <div class="intro-time-stepper">
                    <button type="button" class="intro-time-step-btn" aria-label="增加秒数" @click="adjustTimeLimit(5)">+</button>
                    <button type="button" class="intro-time-step-btn" aria-label="减少秒数" @click="adjustTimeLimit(-5)">−</button>
                  </div>
                  <span class="intro-time-suffix">秒</span>
                </template>
              </div>
            </div>
          </div>
          <el-button type="primary" size="large" class="intro-start-btn" @click="startExercise">
            开始练习
          </el-button>
        </div>

        <div v-else-if="phase === 'summary'" class="summary-page">
          <div class="summary-header">
            <h2>练习完成</h2>
            <p class="summary-score">
              正确 {{ totalCorrect }} / {{ totalQuestions }}（{{ accuracyPercent }}%）
            </p>
            <p class="summary-time">总用时：{{ formatDuration(totalElapsedSeconds) }}</p>
          </div>

          <section
            v-for="(group, groupIndex) in groups"
            :key="'summary-group-' + groupIndex"
            class="summary-group-section"
          >
            <h3 class="summary-group-title">
              第 {{ groupIndex + 1 }} 组
              <span class="summary-group-score">{{ groupResultLabel(groupIndex) }}</span>
            </h3>
            <div
              v-for="slot in group.slots"
              :key="slot.itemKey"
              class="sentence-row"
              :class="summaryRowClass(slot)"
            >
              <p class="slot-word-label">
                <span class="slot-base-word">{{ slot.baseWord }}</span>
              </p>
              <p class="sentence-line">
                <span>{{ slot.sentenceBefore }}</span>
                <span class="cloze-blank" :class="summaryBlankClass(slot)">
                  <span class="blank-filled-word">{{ slot.isCorrect ? slot.correctForm : (slot.studentAnswer || '______') }}</span>
                </span>
                <span>{{ slot.sentenceAfter }}</span>
              </p>
              <div class="result-detail">
                <el-icon v-if="slot.isCorrect" class="icon-correct"><CircleCheckFilled /></el-icon>
                <template v-else>
                  <el-icon class="icon-wrong"><CircleCloseFilled /></el-icon>
                  <span class="correct-answer-hint">
                    正确答案：<strong>{{ slot.correctForm }}</strong>
                  </span>
                </template>
              </div>
            </div>
          </section>

          <div class="summary-actions">
            <template v-if="isAssignmentSession">
              <el-button type="primary" @click="goAssignmentList">返回作业列表</el-button>
              <el-button
                type="warning"
                :disabled="!hasWrongItems"
                @click="assignNirvanaFromWrongItems"
              >
                Nirvana
              </el-button>
            </template>
            <template v-else>
              <el-button type="primary" @click="restartExercise">再来一遍</el-button>
              <el-button type="warning" :disabled="!hasWrongItems" @click="startNirvana">Nirvana</el-button>
              <el-button @click="goHome">返回</el-button>
            </template>
          </div>
        </div>

        <template v-else>
          <div class="cloze-toolbar">
            <span class="group-label">
              <template v-if="isNirvanaSession">Nirvana · </template>
              第 {{ currentGroupIndex + 1 }} / {{ groups.length }} 组
            </span>
            <div class="timer-row">
              <span>剩余时间</span>
              <voc-count-down
                ref="vocCountDown"
                @on-count-down-finished="handleTimeUp"
              />
            </div>
          </div>

          <div v-if="phase === 'groupResult'" class="result-banner">
            本组正确 {{ currentGroupCorrect }} / {{ currentGroup.slots.length }}
          </div>

          <section class="sentences-section">
            <h3>例句</h3>
            <div
              v-for="slot in currentGroup.slots"
              :key="slot.itemKey"
              class="sentence-row"
              :class="resultRowClass(slot)"
            >
              <p class="sentence-line">
                <span>{{ slot.sentenceBefore }}</span>
                <span class="letter-blank" :class="blankClass(slot)">
                  <span class="given-letter">{{ slot.firstLetter }}</span>
                  <template v-if="phase === 'exercise'">
                    <input
                      v-for="(ch, charIndex) in slot.restChars"
                      :key="slot.itemKey + '-' + charIndex"
                      class="letter-input"
                      type="text"
                      maxlength="1"
                      :value="slot.letterInputs[charIndex]"
                      :data-slot-key="slot.itemKey"
                      :data-char-index="charIndex"
                      @focus="onLetterFocus($event)"
                      @input="onLetterInput(slot, charIndex, $event)"
                      @keydown="onLetterKeydown(slot, charIndex, $event)"
                    />
                  </template>
                  <template v-else>
                    <span
                      v-for="(ch, charIndex) in slot.restChars"
                      :key="'r-' + slot.itemKey + '-' + charIndex"
                      class="letter-result"
                      :class="letterResultClass(slot, charIndex)"
                    >{{ slot.letterInputs[charIndex] || '_' }}</span>
                  </template>
                </span>
                <span>{{ slot.sentenceAfter }}</span>
              </p>
              <div v-if="phase === 'groupResult'" class="result-detail">
                <el-icon v-if="slot.isCorrect" class="icon-correct"><CircleCheckFilled /></el-icon>
                <template v-else>
                  <el-icon class="icon-wrong"><CircleCloseFilled /></el-icon>
                  <span class="correct-answer-hint">
                    正确答案：<strong>{{ slot.correctForm }}</strong>
                  </span>
                </template>
              </div>
            </div>
          </section>

          <div class="cloze-actions">
            <el-button
              v-if="phase === 'exercise'"
              type="primary"
              size="large"
              :disabled="!allSlotsFilled"
              @click="submitCurrentGroup"
            >
              提交本组
            </el-button>
            <el-button
              v-else-if="phase === 'groupResult'"
              type="primary"
              size="large"
              @click="goNextGroup"
            >
              {{ isLastGroup ? '查看总结果' : '下一组' }}
            </el-button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import VocCountDown from '../recordtotest/VocCountDown.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  VOC_WORD_COMPLETION_STORAGE_KEY,
  DEFAULT_COMPLETION_TIME_LIMIT_SECONDS,
  buildCompletionGroups,
  isCompletionSlotFilled,
  gradeCompletionSlot,
  collectWrongItemsFromGroups,
  nirvanaSetName,
  isCompletionType,
  COMPLETION_TYPE,
  normalizeCompletionAssignmentGroups,
  itemsFromCompletionGroups,
  buildVocStudentCompletionTestPayload,
  buildVocCompletionAssignmentResultPayload
} from './completionUtils'

export default {
  name: 'WordCompletion',
  components: { VocCountDown },

  data () {
    return {
      payload: null,
      loadError: '',
      groups: [],
      groupSize: 5,
      timeLimitSeconds: DEFAULT_COMPLETION_TIME_LIMIT_SECONDS,
      phase: 'intro',
      currentGroupIndex: 0,
      groupResults: [],
      totalElapsedSeconds: 0,
      groupStartedAt: null,
      activeItems: null,
      isNirvanaSession: false,
      assignmentSourceGroups: [],
      assignmentLoading: false,
      completionTestSubmitted: false
    }
  },

  computed: {
    practiceItems () {
      return this.activeItems || this.payload?.items || []
    },
    hasWrongItems () {
      return this.collectWrongItems().length > 0
    },
    currentGroup () {
      return this.groups[this.currentGroupIndex] || { slots: [], wordBank: [] }
    },
    allSlotsFilled () {
      return this.currentGroup.slots.every((slot) => isCompletionSlotFilled(slot))
    },
    isLastGroup () {
      return this.currentGroupIndex >= this.groups.length - 1
    },
    currentGroupCorrect () {
      return this.currentGroup.slots.filter((slot) => slot.isCorrect).length
    },
    totalCorrect () {
      return this.groupResults.reduce((sum, group) => sum + group.correct, 0)
    },
    totalQuestions () {
      return this.groupResults.reduce((sum, group) => sum + group.total, 0)
    },
    exerciseItemCount () {
      return this.groups.reduce((sum, group) => sum + group.slots.length, 0)
    },
    accuracyPercent () {
      if (!this.totalQuestions) return 0
      return Math.round((this.totalCorrect / this.totalQuestions) * 100)
    },
    isAssignmentSession () {
      return Number(this.payload?.assignmentId) > 0
    }
  },

  mounted () {
    this.loadPayload()
  },

  beforeUnmount () {
    this.stopTimer()
  },

  methods: {
    loadPayload () {
      const assignmentId = Number(this.$route.query.assignmentId)
      if (Number.isFinite(assignmentId) && assignmentId > 0) {
        this.loadAssignment(assignmentId)
        return
      }
      try {
        const raw = sessionStorage.getItem(VOC_WORD_COMPLETION_STORAGE_KEY)
        if (!raw) {
          this.loadError = '未找到词汇数据，请从单词卡页面进入完形填空。'
          return
        }
        this.payload = JSON.parse(raw)
        this.assignmentSourceGroups = []
        this.timeLimitSeconds =
          Number(this.payload.timeLimitSeconds) || DEFAULT_COMPLETION_TIME_LIMIT_SECONDS
        this.groups = buildCompletionGroups(this.payload.items || [], this.groupSize)
        if (!this.groups.length) {
          this.loadError = '没有可在例句中匹配到的词汇，请确认单词和例句是否对应。'
        }
      } catch (e) {
        this.loadError = '加载词汇数据失败，请从单词卡页面重新进入。'
      }
    },

    loadAssignment (assignmentId) {
      this.assignmentLoading = true
      this.$store
        .dispatch('VocClozeAssignment/getById', assignmentId)
        .then((response) => {
          const assignment = response?.data
          if (!assignment) {
            this.loadError = '未找到该完形填空作业。'
            return
          }
          if (!isCompletionType(assignment)) {
            this.loadError = '该作业是选词填空，请从 Assignment 进入。'
            return
          }
          const groups = normalizeCompletionAssignmentGroups(assignment.groups)
          this.assignmentSourceGroups = groups
          this.groups = JSON.parse(JSON.stringify(groups))
          this.groupSize = assignment.groupSize || 5
          this.timeLimitSeconds =
            Number(assignment.timeLimitSeconds) || DEFAULT_COMPLETION_TIME_LIMIT_SECONDS
          this.payload = {
            assignmentId: assignment.id,
            studentId: assignment.studentId,
            subjectName: assignment.subjectName || '',
            setName: assignment.setName || '',
            type: assignment.type || COMPLETION_TYPE,
            items: itemsFromCompletionGroups(groups)
          }
          if (!this.groups.length) {
            this.loadError = '该作业没有可练习的题目。'
          }
        })
        .catch((err) => {
          console.error(err)
          const d = err.response?.data
          this.loadError =
            (typeof d === 'string' && d) ||
            d?.message ||
            d?.title ||
            err.message ||
            '加载完形填空作业失败'
        })
        .finally(() => {
          this.assignmentLoading = false
        })
    },

    resetGroupsForSession () {
      if (this.isAssignmentSession && this.assignmentSourceGroups.length) {
        this.groups = JSON.parse(JSON.stringify(this.assignmentSourceGroups))
        return
      }
      this.groups = buildCompletionGroups(this.practiceItems, this.groupSize)
    },

    startExercise () {
      this.phase = 'exercise'
      this.currentGroupIndex = 0
      this.groupResults = []
      this.totalElapsedSeconds = 0
      this.isNirvanaSession = !!this.payload?.isNirvanaSession
      this.activeItems = this.payload?.isNirvanaSession ? this.practiceItems : null
      this.completionTestSubmitted = false
      this.resetGroupsForSession()
      this.prepareGroup()
    },

    restartExercise () {
      this.resetGroupsForSession()
      this.phase = 'exercise'
      this.currentGroupIndex = 0
      this.groupResults = []
      this.totalElapsedSeconds = 0
      this.completionTestSubmitted = false
      this.prepareGroup()
    },

    startNirvana () {
      const wrongItems = this.collectWrongItems()
      if (!wrongItems.length) {
        ElMessage({ message: '全部正确，无需 Nirvana', type: 'success' })
        return
      }
      this.activeItems = wrongItems
      this.isNirvanaSession = true
      this.groups = buildCompletionGroups(wrongItems, this.groupSize)
      this.phase = 'exercise'
      this.currentGroupIndex = 0
      this.groupResults = []
      this.totalElapsedSeconds = 0
      this.prepareGroup()
    },

    collectWrongItems () {
      return collectWrongItemsFromGroups(this.groups)
    },

    assignNirvanaFromWrongItems () {
      const wrongItems = this.collectWrongItems()
      if (!wrongItems.length) {
        ElMessage({ message: '全部正确，无需 Nirvana', type: 'success' })
        return
      }
      const groups = buildCompletionGroups(wrongItems, this.groupSize)
      const questionCount = groups.reduce((sum, group) => sum + group.slots.length, 0)
      ElMessageBox.prompt(
        `将本次 ${questionCount} 道错题重新布置为 Nirvana（共 ${groups.length} 组）。请填写每组限时（秒）。`,
        'Nirvana',
        {
          confirmButtonText: '开始',
          cancelButtonText: '取消',
          inputValue: String(this.timeLimitSeconds || DEFAULT_COMPLETION_TIME_LIMIT_SECONDS),
          inputPlaceholder: '每组限时（秒）',
          inputType: 'number',
          inputValidator: (value) => {
            const seconds = Number(value)
            if (!Number.isFinite(seconds) || seconds < 1) {
              return '请输入每组限时，至少 1 秒'
            }
            return true
          }
        }
      )
        .then(({ value }) => {
          const timeLimitSeconds = Math.max(1, Math.round(Number(value)))
          const nextPayload = {
            subjectName: this.payload?.subjectName || '',
            setName: nirvanaSetName(this.payload?.setName),
            studentId:
              Number(this.payload?.studentId) ||
              Number(localStorage.getItem('targetId')) ||
              Number(localStorage.getItem('userId')) ||
              0,
            items: wrongItems,
            timeLimitSeconds,
            isNirvanaSession: true
          }
          sessionStorage.setItem(VOC_WORD_COMPLETION_STORAGE_KEY, JSON.stringify(nextPayload))
          this.payload = nextPayload
          this.assignmentSourceGroups = []
          this.activeItems = wrongItems
          this.isNirvanaSession = true
          this.timeLimitSeconds = timeLimitSeconds
          this.groups = groups
          this.phase = 'intro'
          this.currentGroupIndex = 0
          this.groupResults = []
          this.totalElapsedSeconds = 0
          ElMessage.success('已根据错题生成 Nirvana 练习')
        })
        .catch((err) => {
          if (err === 'cancel' || err === 'close') return
        })
    },

    prepareGroup () {
      this.groupStartedAt = Date.now()
      this.$nextTick(() => {
        this.startTimer()
        this.focusFirstEmptyInput()
      })
    },

    startTimer () {
      const seconds = Math.max(1, Number(this.timeLimitSeconds) || DEFAULT_COMPLETION_TIME_LIMIT_SECONDS)
      this.timeLimitSeconds = seconds
      if (this.$refs.vocCountDown) {
        this.$refs.vocCountDown.countdown(seconds, true)
      }
    },

    adjustTimeLimit (delta) {
      const current = Number(this.timeLimitSeconds)
      const base = Number.isFinite(current) ? current : DEFAULT_COMPLETION_TIME_LIMIT_SECONDS
      this.timeLimitSeconds = Math.max(1, base + delta)
    },

    stopTimer () {
      if (this.$refs.vocCountDown) {
        this.$refs.vocCountDown.countdown(0, false)
      }
    },

    recordGroupElapsed () {
      if (!this.groupStartedAt) return 0
      const elapsed = Math.max(0, Math.round((Date.now() - this.groupStartedAt) / 1000))
      this.totalElapsedSeconds += elapsed
      this.groupStartedAt = null
      return elapsed
    },

    handleTimeUp () {
      if (this.phase === 'exercise') {
        this.submitCurrentGroup(true)
      }
    },

    onLetterFocus (event) {
      event.target.select()
    },

    fillLetter (slot, charIndex, ch, inputEl) {
      const letter = String(ch || '').slice(-1)
      slot.letterInputs.splice(charIndex, 1, letter)
      if (inputEl) inputEl.value = letter
      if (letter && charIndex < slot.restChars.length - 1) {
        this.focusLetterInput(slot.itemKey, charIndex + 1)
      }
    },

    onLetterInput (slot, charIndex, event) {
      this.fillLetter(slot, charIndex, event.target.value, event.target)
    },

    onLetterKeydown (slot, charIndex, event) {
      if (event.key === 'Backspace') {
        if (slot.letterInputs[charIndex]) {
          event.preventDefault()
          this.fillLetter(slot, charIndex, '', event.target)
          return
        }
        if (charIndex > 0) {
          event.preventDefault()
          this.focusLetterInput(slot.itemKey, charIndex - 1)
        }
        return
      }
      if (event.key === 'ArrowLeft' && charIndex > 0) {
        event.preventDefault()
        this.focusLetterInput(slot.itemKey, charIndex - 1)
        return
      }
      if (event.key === 'ArrowRight' && charIndex < slot.restChars.length - 1) {
        event.preventDefault()
        this.focusLetterInput(slot.itemKey, charIndex + 1)
        return
      }
      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault()
        this.fillLetter(slot, charIndex, event.key, event.target)
      }
    },

    focusLetterInput (slotKey, charIndex) {
      this.$nextTick(() => {
        const el = this.$el.querySelector(
          `input.letter-input[data-slot-key="${CSS.escape(slotKey)}"][data-char-index="${charIndex}"]`
        )
        if (!el) return
        el.focus()
        el.select()
      })
    },

    focusFirstEmptyInput () {
      const slot = this.currentGroup.slots.find((item) => !isCompletionSlotFilled(item))
      if (!slot) return
      const idx = (slot.letterInputs || []).findIndex((ch) => !ch)
      this.focusLetterInput(slot.itemKey, idx < 0 ? 0 : idx)
    },

    submitCurrentGroup (timedOut = false) {
      this.stopTimer()
      const elapsedSeconds = this.recordGroupElapsed()
      let correct = 0
      this.currentGroup.slots.forEach((slot) => {
        if (gradeCompletionSlot(slot)) correct += 1
      })
      this.groupResults.push({
        groupIndex: this.currentGroupIndex,
        correct,
        total: this.currentGroup.slots.length,
        timedOut,
        elapsedSeconds
      })
      this.phase = 'groupResult'
    },

    goNextGroup () {
      if (this.isLastGroup) {
        this.phase = 'summary'
        this.postStudentCompletionTest()
        return
      }
      this.currentGroupIndex += 1
      this.phase = 'exercise'
      this.prepareGroup()
    },

    getStudentId () {
      const raw =
        this.payload?.studentId ||
        localStorage.getItem('targetId') ||
        localStorage.getItem('userId')
      const studentId = Number(raw)
      return Number.isFinite(studentId) && studentId > 0 ? studentId : null
    },

    postStudentCompletionTest () {
      if (this.completionTestSubmitted) return
      const studentId = this.getStudentId()
      if (!studentId) {
        ElMessage.warning('未找到学生 ID，无法提交完形填空结果')
        return
      }

      const postData = this.isAssignmentSession
        ? buildVocCompletionAssignmentResultPayload({
            payload: this.payload,
            groups: this.groups,
            groupResults: this.groupResults,
            studentId,
            createdBy: Number(localStorage.getItem('userId')) || studentId,
            assignmentId: this.payload.assignmentId,
            isNirvanaSession: this.isNirvanaSession,
            groupSize: this.groupSize,
            timeLimitSeconds: this.timeLimitSeconds,
            totalElapsedSeconds: this.totalElapsedSeconds,
            totalCorrect: this.totalCorrect,
            totalQuestions: this.totalQuestions,
            accuracyPercent: this.accuracyPercent
          })
        : buildVocStudentCompletionTestPayload({
            payload: this.payload,
            groups: this.groups,
            groupResults: this.groupResults,
            studentId,
            createdBy: Number(localStorage.getItem('userId')) || studentId,
            isNirvanaSession: this.isNirvanaSession,
            groupSize: this.groupSize,
            timeLimitSeconds: this.timeLimitSeconds,
            totalElapsedSeconds: this.totalElapsedSeconds,
            totalCorrect: this.totalCorrect,
            totalQuestions: this.totalQuestions,
            accuracyPercent: this.accuracyPercent
          })

      this.completionTestSubmitted = true
      this.$store
        .dispatch(
          this.isAssignmentSession
            ? 'VocClozeAssignmentResult/add'
            : 'VocStudentClozeTest/add',
          postData
        )
        .then(() => {
          ElMessage.success('完形填空结果已提交')
        })
        .catch((err) => {
          this.completionTestSubmitted = false
          console.error(err)
          const d = err.response?.data
          const firstValidationError = d?.errors
            ? Object.values(d.errors).find((arr) => Array.isArray(arr) && arr.length)?.[0]
            : ''
          const msg =
            (typeof d === 'string' && d) ||
            firstValidationError ||
            d?.message ||
            d?.title ||
            err.message ||
            '提交完形填空结果失败'
          ElMessage.error(String(msg))
        })
    },

    blankClass (slot) {
      return {
        'blank-correct': this.phase === 'groupResult' && slot.isCorrect,
        'blank-wrong': this.phase === 'groupResult' && !slot.isCorrect
      }
    },

    letterResultClass (slot, charIndex) {
      const expected = String(slot.restChars[charIndex] || '')
      const actual = String(slot.letterInputs[charIndex] || '')
      if (actual.toLowerCase() === expected.toLowerCase()) return 'letter-ok'
      return 'letter-bad'
    },

    resultRowClass (slot) {
      if (this.phase !== 'groupResult') return {}
      return {
        'row-correct': slot.isCorrect,
        'row-wrong': !slot.isCorrect
      }
    },

    summaryRowClass (slot) {
      return {
        'row-correct': slot.isCorrect,
        'row-wrong': !slot.isCorrect
      }
    },

    summaryBlankClass (slot) {
      return {
        'blank-filled': true,
        'blank-correct': slot.isCorrect,
        'blank-wrong': !slot.isCorrect
      }
    },

    groupResultLabel (groupIndex) {
      const result = this.groupResults.find((item) => item.groupIndex === groupIndex)
      if (!result) return ''
      return `${result.correct} / ${result.total} 正确`
    },

    formatDuration (seconds) {
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${m} 分 ${s} 秒`
    },

    goHome () {
      this.$router.push('/')
    },

    goAssignmentList () {
      this.$router.push({ path: '/blankassignment' })
    }
  }
}
</script>

<style scoped>
.word-cloze-page {
  min-height: 100vh;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
}

.navi-text {
  margin-left: 1rem;
  color: #666;
  font-size: 0.95rem;
}

.andover-logo-area {
  cursor: pointer;
}

.cloze-content {
  max-width: 820px;
  margin: 0 auto;
  padding: 1.5rem;
}

.cloze-panel {
  text-align: center;
  padding: 2rem 1rem;
}

.intro-desc {
  color: #666;
  margin: 1rem 0;
}

.intro-notice {
  text-align: left;
  max-width: 560px;
  margin: 0 auto 1.25rem;
  padding: 0.85rem 1rem;
  background: #fff8f8;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  color: #555;
  font-size: 0.92rem;
  line-height: 1.65;
}

.intro-config-card {
  max-width: 520px;
  margin: 1.5rem auto 1.75rem;
  padding: 1rem 1.1rem 1.1rem;
  background: linear-gradient(180deg, #f7fcff 0%, #ffffff 100%);
  border: 1px solid #d6f0fa;
  border-radius: 12px;
}

.intro-config-grid {
  display: flex;
  justify-content: space-between;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px dashed #cceeff;
}

.intro-stat-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.intro-stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: deepskyblue;
}

.intro-stat-label {
  font-size: 0.82rem;
  color: #667788;
}

.intro-stat-divider {
  width: 1px;
  background: rgba(0, 191, 255, 0.35);
}

.intro-time-row {
  display: flex;
  gap: 0.75rem;
  --intro-time-control-height: 36px;
}

.intro-time-label-shell,
.intro-time-input-shell {
  display: flex;
  align-items: stretch;
  height: var(--intro-time-control-height);
  border: 1px solid #b8e6f8;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.intro-time-label-shell {
  flex: 1;
}

.intro-time-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  background: #eef9ff;
  color: #0284c7;
}

.intro-time-text {
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
}

.intro-time-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0284c7;
}

.intro-time-field {
  width: 72px;
  border: none;
  text-align: center;
  font-size: 1rem;
  font-weight: 700;
  color: #0284c7;
  background: transparent;
}

.intro-time-locked {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.intro-time-stepper {
  display: flex;
  flex-direction: column;
  width: 26px;
  border-left: 1px solid #d6f0fa;
}

.intro-time-step-btn {
  flex: 1;
  border: 0;
  border-bottom: 1px solid #d6f0fa;
  background: #f8fdff;
  color: #0284c7;
  cursor: pointer;
}

.intro-time-step-btn:last-child {
  border-bottom: 0;
}

.intro-time-suffix {
  display: flex;
  align-items: center;
  padding: 0 0.65rem;
  background: #eef9ff;
  color: #0284c7;
  font-size: 0.88rem;
  font-weight: 600;
}

.intro-start-btn {
  min-width: 140px;
}

.cloze-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.timer-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.result-banner {
  margin-bottom: 1rem;
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  background: #eef9ff;
  color: #0284c7;
  font-weight: 600;
}

.sentence-row {
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid #eee;
}

.sentence-row.row-correct {
  border-color: #81c784;
  background: #f1f8e9;
}

.sentence-row.row-wrong {
  border-color: #ef9a9a;
  background: #fff5f5;
}

.sentence-line {
  margin: 0;
  line-height: 2;
  font-size: 1.05rem;
}

.letter-blank,
.cloze-blank {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  margin: 0 0.2rem;
  padding: 0.15rem 0.35rem;
  border-bottom: 2px dashed #bbb;
  vertical-align: baseline;
}

.letter-blank.blank-correct,
.cloze-blank.blank-correct {
  border-bottom-color: #43a047;
  background: rgba(67, 160, 71, 0.1);
}

.letter-blank.blank-wrong,
.cloze-blank.blank-wrong {
  border-bottom-color: #e53935;
  background: rgba(229, 57, 53, 0.08);
}

.given-letter {
  font-weight: 800;
  color: #0284c7;
  letter-spacing: 0.04em;
  text-align: center;
  white-space: nowrap;
}

.letter-input {
  width: 1.15rem;
  height: 1.6rem;
  padding: 0;
  border: 1px solid #90caf9;
  border-radius: 4px;
  text-align: center;
  font-size: 1rem;
  font-weight: 700;
  color: #1565c0;
}

.letter-result {
  display: inline-flex;
  width: 1.15rem;
  justify-content: center;
  font-weight: 700;
}

.letter-ok {
  color: #2e7d32;
}

.letter-bad {
  color: #c62828;
}

.blank-filled-word {
  color: deepskyblue;
  font-weight: 700;
}

.slot-word-label {
  margin: 0 0 0.35rem;
}

.slot-base-word {
  font-weight: 700;
  color: deepskyblue;
}

.result-detail {
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.icon-correct {
  color: #43a047;
}

.icon-wrong {
  color: #e53935;
}

.correct-answer-hint {
  color: #c62828;
  font-size: 0.9rem;
}

.cloze-actions {
  text-align: center;
  margin-top: 1.25rem;
}

.summary-page {
  text-align: left;
}

.summary-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.summary-actions {
  text-align: center;
  margin-top: 1.5rem;
}

.summary-group-score {
  margin-left: 0.5rem;
  color: #888;
  font-size: 0.9rem;
}
</style>
